import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { orderTypePieChartUrl } from "../../assets/api/BaseURL";

ChartJS.register(ArcElement, Tooltip, Legend);

const OrderTypePieChartDashboard = ({ token }) => {
  // ✅ Default = Last 30 Days
  const [range, setRange] = useState("last_30_days");
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /* 📡 Fetch API */
  useEffect(() => {
    if (!token) return;

    const getData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${orderTypePieChartUrl}?range=${range}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 && response.data?.data) {
          setDashboardData(response.data.data);
        } else {
          setDashboardData(null);
          toast.info("No data found");
        }
      } catch (error) {
        setDashboardData(null);
        toast.error(
          error?.response?.data?.message || error.message
        );
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [token, range]);

  /* 🥧 Chart Data */
  const chartData = useMemo(() => {
    if (!dashboardData?.labels) {
      return { labels: [], datasets: [] };
    }

    return {
      labels: dashboardData.labels,
      datasets: [
        {
          label: "Order Type Distribution",
          data: dashboardData.orders,
          backgroundColor: [
            "#4CAF50", // Dine In
            "#FFC107", // Take Away
            "#36A2EB", // Delivery
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [dashboardData]);

  /* ❌ No Data Condition */
  const isNoData =
    !dashboardData ||
    !dashboardData.orders ||
    dashboardData.orders.every((item) => item === 0);

  return (
    <div className="bg-white rounded shadow-sm">

      {/* 🔷 Header */}
      <div className="d-flex justify-content-between align-items-center mx-3 my-3">
        <h6 className="mb-0">Order Types</h6>

        {/* ✅ Updated Filters */}
        <select
          className="form-select form-select-sm w-auto"
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="this_month">This Month</option>
          <option value="last_month">Last Month</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>
      </div>

      {/* 📊 Chart / No Data */}
      <div className="card-body text-center">
        {isLoading ? (
          <p className="mb-0">Loading...</p>
        ) : isNoData ? (
          <div>
            <p className="mb-1 text-muted">No Data Available</p>
            <small className="text-muted">
              No orders found for selected range
            </small>
          </div>
        ) : (
          <Pie
            data={chartData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "bottom",
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      const value = context.raw;
                      const index = context.dataIndex;
                      const percentage =
                        dashboardData?.percentages?.[index] || 0;

                      return `${context.label}: ${value} (${percentage}%)`;
                    },
                  },
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OrderTypePieChartDashboard;